"use client";

import { SessionEntityKind } from "@cabal/shared/domain/session/enums.js";
import type { CollabPreview } from "@cabal/shared/domain/session/collab/types.js";
import type { ProductSummary } from "@cabal/shared/domain/session/product/types.js";
import type {
  VersionPreview,
  VersionSummary,
} from "@cabal/shared/domain/session/version/types.js";
import type { AttachedFileView } from "@cabal/shared/domain/upload/types.js";
import { ChatMode, type ChatState } from "@/types/frontend-types";
import type { ResolvedShortId } from "../data/entity-lookup-selectors";

export interface CollabRef {
  id: string;
  onProduct: string | null;
  onVersion: string | null;
}

type ResolveShortId = (
  shortId: string | null | undefined,
) => ResolvedShortId | null;

export enum ProductReferenceResolutionKind {
  Ready = "ready",
  ProductUnresolved = "product-unresolved",
  ShortIdUnresolved = "short-id-unresolved",
}

export interface ProductReferenceResolutionReady {
  kind: ProductReferenceResolutionKind.Ready;
  currentFocusedParentProductId: string | null;
  productId: string;
  targetParentProductId: string | null;
}

export interface ProductReferenceResolutionProductUnresolved {
  kind: ProductReferenceResolutionKind.ProductUnresolved;
  productId: string;
}

export interface ProductReferenceResolutionShortIdUnresolved {
  kind: ProductReferenceResolutionKind.ShortIdUnresolved;
}

export type ProductReferenceResolution =
  | ProductReferenceResolutionReady
  | ProductReferenceResolutionProductUnresolved
  | ProductReferenceResolutionShortIdUnresolved;

export enum VersionReferenceResolutionKind {
  Ready = "ready",
  ProductUnresolved = "product-unresolved",
  ShortIdUnresolved = "short-id-unresolved",
}

export interface VersionReferenceResolutionReady {
  kind: VersionReferenceResolutionKind.Ready;
  displayedVersionId: string | null;
  productId: string;
  targetParentProductId: string | null;
  versionId: string;
}

export interface VersionReferenceResolutionProductUnresolved {
  kind: VersionReferenceResolutionKind.ProductUnresolved;
  productId: string | null;
  versionId: string;
}

export interface VersionReferenceResolutionShortIdUnresolved {
  kind: VersionReferenceResolutionKind.ShortIdUnresolved;
}

export type VersionReferenceResolution =
  | VersionReferenceResolutionReady
  | VersionReferenceResolutionProductUnresolved
  | VersionReferenceResolutionShortIdUnresolved;

export enum CollabReferenceResolutionKind {
  Ready = "ready",
  CollabUnresolved = "collab-unresolved",
  ShortIdUnresolved = "short-id-unresolved",
}

export interface CollabReferenceResolutionReady {
  kind: CollabReferenceResolutionKind.Ready;
  collab: CollabRef;
}

export interface CollabReferenceResolutionCollabUnresolved {
  kind: CollabReferenceResolutionKind.CollabUnresolved;
  collabId: string;
}

export interface CollabReferenceResolutionShortIdUnresolved {
  kind: CollabReferenceResolutionKind.ShortIdUnresolved;
}

export type CollabReferenceResolution =
  | CollabReferenceResolutionReady
  | CollabReferenceResolutionCollabUnresolved
  | CollabReferenceResolutionShortIdUnresolved;

export enum FileReferenceResolutionKind {
  Ready = "ready",
  FileUnresolved = "file-unresolved",
  ProductUnresolved = "product-unresolved",
  ShortIdUnresolved = "short-id-unresolved",
}

export interface FileReferenceResolutionReady {
  kind: FileReferenceResolutionKind.Ready;
  file: AttachedFileView;
  files: AttachedFileView[];
  productId: string;
  targetParentProductId: string | null;
  versionId: string | null;
}

export interface FileReferenceResolutionFileUnresolved {
  kind: FileReferenceResolutionKind.FileUnresolved;
  fileId: string;
}

export interface FileReferenceResolutionProductUnresolved {
  kind: FileReferenceResolutionKind.ProductUnresolved;
  file: AttachedFileView;
  productId: string | null;
}

export interface FileReferenceResolutionShortIdUnresolved {
  kind: FileReferenceResolutionKind.ShortIdUnresolved;
}

export type FileReferenceResolution =
  | FileReferenceResolutionReady
  | FileReferenceResolutionFileUnresolved
  | FileReferenceResolutionProductUnresolved
  | FileReferenceResolutionShortIdUnresolved;

interface ResolveProductReferenceTargetInput {
  focusedProductId: string | null;
  products: Record<string, ProductSummary>;
  resolveShortId: ResolveShortId;
  shortId: string | null | undefined;
}

interface ResolveVersionReferenceTargetInput extends ResolveProductReferenceTargetInput {
  selectedVersionId: string | null;
  versionPreviews: Record<string, VersionPreview>;
  versions: Record<string, VersionSummary>;
}

interface ResolveCollabReferenceTargetInput {
  chatMessages: Record<
    string,
    { id: string; onProduct: string | null; onVersion: string | null }
  >;
  collabPreviews: Record<string, CollabPreview>;
  collabs: Record<
    string,
    { id: string; onProduct: string | null; onVersion: string | null }
  >;
  resolveShortId: ResolveShortId;
  shortId: string | null | undefined;
}

interface IsCollabAlreadyTargetedInput {
  chat: Pick<
    ChatState,
    "mode" | "selectedProductId" | "targetMessageId"
  >;
  collab: CollabRef;
}

interface ResolveFileReferenceTargetInput {
  attachedFiles: Record<string, AttachedFileView>;
  chatMessages: Record<
    string,
    { id: string; onProduct: string | null; onVersion: string | null }
  >;
  collabPreviews: Record<string, CollabPreview>;
  collabs: Record<
    string,
    { id: string; onProduct: string | null; onVersion: string | null }
  >;
  products: Record<string, ProductSummary>;
  resolveShortId: ResolveShortId;
  shortId: string | null | undefined;
  versionPreviews: Record<string, VersionPreview>;
  versions: Record<string, VersionSummary>;
}

interface ResolveVersionReferenceProductIdInput {
  products: Record<string, ProductSummary>;
  versionId: string;
  versionPreviews: Record<string, VersionPreview>;
  versions: Record<string, VersionSummary>;
}

interface DisplayedVersionInput {
  focusedProductId: string;
  selectedVersionId: string | null;
  versionPreviews: Record<string, VersionPreview>;
  versions: Record<string, VersionSummary>;
}

interface VersionCandidate {
  id: string;
  isSeededPlaceholder: boolean | undefined;
  productId: string;
  versionNumber: number;
}

interface NewestVersionSelection {
  newestAny: VersionCandidate | null;
  newestWritten: VersionCandidate | null;
}

export function resolveProductReferenceTarget({
  focusedProductId,
  products,
  resolveShortId,
  shortId,
}: ResolveProductReferenceTargetInput): ProductReferenceResolution {
  const resolved = resolveShortId(shortId);
  if (!isResolvedEntityKind(resolved, SessionEntityKind.Product)) {
    return { kind: ProductReferenceResolutionKind.ShortIdUnresolved };
  }

  const product = products[resolved.entityId] ?? null;
  if (!product) {
    return {
      kind: ProductReferenceResolutionKind.ProductUnresolved,
      productId: resolved.entityId,
    };
  }

  return {
    kind: ProductReferenceResolutionKind.Ready,
    currentFocusedParentProductId: resolveProductParentId({
      productId: focusedProductId,
      products,
    }),
    productId: product.id,
    targetParentProductId: product.parentProductId,
  };
}

export function resolveVersionReferenceTarget({
  focusedProductId,
  products,
  resolveShortId,
  selectedVersionId,
  shortId,
  versionPreviews,
  versions,
}: ResolveVersionReferenceTargetInput): VersionReferenceResolution {
  const resolved = resolveShortId(shortId);
  if (!isResolvedEntityKind(resolved, SessionEntityKind.Version)) {
    return { kind: VersionReferenceResolutionKind.ShortIdUnresolved };
  }

  const versionId = resolved.entityId;
  const productId = resolveVersionReferenceProductId({
    products,
    versionId,
    versionPreviews,
    versions,
  });
  const product = resolveKnownProduct({ productId, products });

  if (!product) {
    return {
      kind: VersionReferenceResolutionKind.ProductUnresolved,
      productId,
      versionId,
    };
  }

  return {
    kind: VersionReferenceResolutionKind.Ready,
    displayedVersionId: resolveDisplayedVersionIdForTarget({
      focusedProductId,
      productId: product.id,
      selectedVersionId,
      versionPreviews,
      versions,
    }),
    productId: product.id,
    targetParentProductId: product.parentProductId,
    versionId,
  };
}

export function resolveCollabReferenceTarget({
  chatMessages,
  collabPreviews,
  collabs,
  resolveShortId,
  shortId,
}: ResolveCollabReferenceTargetInput): CollabReferenceResolution {
  const resolved = resolveShortId(shortId);
  if (!isResolvedEntityKind(resolved, SessionEntityKind.Collab)) {
    return { kind: CollabReferenceResolutionKind.ShortIdUnresolved };
  }

  const collab = pickCollabRef({
    chatMessages,
    collabPreviews,
    collabs,
    id: resolved.entityId,
  });
  if (!collab) {
    return {
      kind: CollabReferenceResolutionKind.CollabUnresolved,
      collabId: resolved.entityId,
    };
  }

  return { kind: CollabReferenceResolutionKind.Ready, collab };
}

export function isCollabAlreadyTargeted({
  chat,
  collab,
}: IsCollabAlreadyTargetedInput): boolean {
  if (chat.targetMessageId !== collab.id) {
    return false;
  }

  if (collab.onProduct == null) {
    return chat.mode === ChatMode.Main;
  }

  return (
    chat.mode === ChatMode.Product &&
    chat.selectedProductId === collab.onProduct
  );
}

export function resolveFileReferenceTarget({
  attachedFiles,
  chatMessages,
  collabPreviews,
  collabs,
  products,
  resolveShortId,
  shortId,
  versionPreviews,
  versions,
}: ResolveFileReferenceTargetInput): FileReferenceResolution {
  const resolved = resolveShortId(shortId);
  if (!isResolvedEntityKind(resolved, SessionEntityKind.File)) {
    return { kind: FileReferenceResolutionKind.ShortIdUnresolved };
  }

  const file = attachedFiles[resolved.entityId] ?? resolved.file ?? null;
  if (!file) {
    return {
      kind: FileReferenceResolutionKind.FileUnresolved,
      fileId: resolved.entityId,
    };
  }

  const productId = resolveFileReferenceProductId({
    file,
    chatMessages,
    collabPreviews,
    collabs,
    products,
    versionPreviews,
    versions,
  });
  const product = resolveKnownProduct({ productId, products });

  if (!product) {
    return {
      kind: FileReferenceResolutionKind.ProductUnresolved,
      file,
      productId,
    };
  }

  return {
    kind: FileReferenceResolutionKind.Ready,
    file,
    files: selectFilePreviewFiles({ attachedFiles, file }),
    productId: product.id,
    targetParentProductId: product.parentProductId,
    versionId: file.versionId,
  };
}

function resolveDisplayedVersionIdForTarget(input: {
  focusedProductId: string | null;
  productId: string;
  selectedVersionId: string | null;
  versionPreviews: Record<string, VersionPreview>;
  versions: Record<string, VersionSummary>;
}): string | null {
  if (!input.focusedProductId || input.focusedProductId !== input.productId) {
    return null;
  }
  return resolveDisplayedVersionIdOfFocusedProduct({
    focusedProductId: input.focusedProductId,
    selectedVersionId: input.selectedVersionId,
    versionPreviews: input.versionPreviews,
    versions: input.versions,
  });
}

function resolveDisplayedVersionIdOfFocusedProduct({
  focusedProductId,
  selectedVersionId,
  versionPreviews,
  versions,
}: DisplayedVersionInput): string | null {
  if (selectedVersionId) {
    return selectedVersionId;
  }

  return selectFocusedFallbackVersionId({
    focusedProductId,
    versionPreviews,
    versions,
  });
}

function selectFocusedFallbackVersionId(input: {
  focusedProductId: string;
  versionPreviews: Record<string, VersionPreview>;
  versions: Record<string, VersionSummary>;
}): string | null {
  const selection = getFocusedVersionCandidates({
    focusedProductId: input.focusedProductId,
    versionPreviews: input.versionPreviews,
    versions: input.versions,
  }).reduce(selectNewestVersionCandidate, {
    newestAny: null,
    newestWritten: null,
  });

  return selectNewestVersionId(selection);
}

function selectNewestVersionId(
  selection: NewestVersionSelection,
): string | null {
  if (selection.newestWritten) {
    return selection.newestWritten.id;
  }
  if (selection.newestAny) {
    return selection.newestAny.id;
  }
  return null;
}

function getFocusedVersionCandidates(input: {
  focusedProductId: string;
  versionPreviews: Record<string, VersionPreview>;
  versions: Record<string, VersionSummary>;
}): VersionCandidate[] {
  return [
    ...Object.values(input.versions),
    ...Object.values(input.versionPreviews),
  ]
    .filter((version) => version.productId === input.focusedProductId)
    .map((version) => ({
      id: version.id,
      isSeededPlaceholder: version.isSeededPlaceholder,
      productId: version.productId,
      versionNumber: version.versionNumber,
    }));
}

function selectNewestVersionCandidate(
  selection: NewestVersionSelection,
  candidate: VersionCandidate,
): NewestVersionSelection {
  const newestAny = pickNewerVersionCandidate(selection.newestAny, candidate);
  const newestWritten = candidate.isSeededPlaceholder
    ? selection.newestWritten
    : pickNewerVersionCandidate(selection.newestWritten, candidate);

  return { newestAny, newestWritten };
}

function pickNewerVersionCandidate(
  current: VersionCandidate | null,
  candidate: VersionCandidate,
): VersionCandidate {
  if (!current) {
    return candidate;
  }
  if (candidate.versionNumber > current.versionNumber) {
    return candidate;
  }
  return current;
}

function resolveVersionReferenceProductId({
  products,
  versionId,
  versionPreviews,
  versions,
}: ResolveVersionReferenceProductIdInput): string | null {
  const cachedVersion = versions[versionId];
  if (cachedVersion) {
    return cachedVersion.productId;
  }

  const previewVersion = versionPreviews[versionId];
  if (previewVersion) {
    return previewVersion.productId;
  }

  return findProductIdContainingVersion({ products, versionId });
}

function findProductIdContainingVersion(input: {
  products: Record<string, ProductSummary>;
  versionId: string;
}): string | null {
  return (
    Object.values(input.products).find((product) =>
      product.versionIds.includes(input.versionId),
    )?.id ?? null
  );
}

function resolveFileReferenceProductId(input: {
  chatMessages: Record<
    string,
    { id: string; onProduct: string | null; onVersion: string | null }
  >;
  collabPreviews: Record<string, CollabPreview>;
  collabs: Record<
    string,
    { id: string; onProduct: string | null; onVersion: string | null }
  >;
  file: AttachedFileView;
  products: Record<string, ProductSummary>;
  versionPreviews: Record<string, VersionPreview>;
  versions: Record<string, VersionSummary>;
}): string | null {
  if (input.file.productId) {
    return input.file.productId;
  }

  if (input.file.versionId) {
    return resolveVersionReferenceProductId({
      products: input.products,
      versionId: input.file.versionId,
      versionPreviews: input.versionPreviews,
      versions: input.versions,
    });
  }

  if (input.file.collabId) {
    return resolveCollabReferenceProductId({
      chatMessages: input.chatMessages,
      collabPreviews: input.collabPreviews,
      collabs: input.collabs,
      collabId: input.file.collabId,
      products: input.products,
      versionPreviews: input.versionPreviews,
      versions: input.versions,
    });
  }

  return null;
}

function resolveCollabReferenceProductId(input: {
  chatMessages: Record<
    string,
    { id: string; onProduct: string | null; onVersion: string | null }
  >;
  collabPreviews: Record<string, CollabPreview>;
  collabs: Record<
    string,
    { id: string; onProduct: string | null; onVersion: string | null }
  >;
  collabId: string;
  products: Record<string, ProductSummary>;
  versionPreviews: Record<string, VersionPreview>;
  versions: Record<string, VersionSummary>;
}): string | null {
  const collab = pickCollabRef({
    chatMessages: input.chatMessages,
    collabPreviews: input.collabPreviews,
    collabs: input.collabs,
    id: input.collabId,
  });

  if (!collab) {
    return null;
  }

  if (collab.onProduct) {
    return collab.onProduct;
  }

  if (!collab.onVersion) {
    return null;
  }

  return resolveVersionReferenceProductId({
    products: input.products,
    versionId: collab.onVersion,
    versionPreviews: input.versionPreviews,
    versions: input.versions,
  });
}

function selectFilePreviewFiles(input: {
  attachedFiles: Record<string, AttachedFileView>;
  file: AttachedFileView;
}): AttachedFileView[] {
  const files = Object.values(input.attachedFiles).filter((file) =>
    isSameFilePreviewContainer({ file, targetFile: input.file }),
  );

  return files.length > 0 ? files : [input.file];
}

function isSameFilePreviewContainer(input: {
  file: AttachedFileView;
  targetFile: AttachedFileView;
}): boolean {
  if (input.targetFile.versionId) {
    return input.file.versionId === input.targetFile.versionId;
  }

  if (input.targetFile.collabId) {
    return input.file.collabId === input.targetFile.collabId;
  }

  if (input.targetFile.productId) {
    return (
      input.file.productId === input.targetFile.productId &&
      !input.file.versionId &&
      !input.file.collabId
    );
  }

  return input.file.id === input.targetFile.id;
}

function pickCollabRef(input: {
  chatMessages: Record<
    string,
    { id: string; onProduct: string | null; onVersion: string | null }
  >;
  collabPreviews: Record<string, CollabPreview>;
  collabs: Record<
    string,
    { id: string; onProduct: string | null; onVersion: string | null }
  >;
  id: string;
}): CollabRef | null {
  const fullCollab = pickFullCollabRef(input);
  if (fullCollab) {
    return fullCollab;
  }
  return pickPreviewCollabRef(input);
}

function pickFullCollabRef(input: {
  chatMessages: Record<
    string,
    { id: string; onProduct: string | null; onVersion: string | null }
  >;
  collabs: Record<
    string,
    { id: string; onProduct: string | null; onVersion: string | null }
  >;
  id: string;
}): CollabRef | null {
  const collab =
    input.collabs[input.id] ?? input.chatMessages[input.id] ?? null;
  if (!collab) {
    return null;
  }
  return {
    id: collab.id,
    onProduct: collab.onProduct,
    onVersion: collab.onVersion,
  };
}

function pickPreviewCollabRef(input: {
  collabPreviews: Record<string, CollabPreview>;
  id: string;
}): CollabRef | null {
  const preview = input.collabPreviews[input.id] ?? null;
  if (!preview) {
    return null;
  }
  return {
    id: preview.id,
    onProduct: preview.onProduct,
    onVersion: preview.onVersion,
  };
}

function resolveKnownProduct(input: {
  productId: string | null;
  products: Record<string, ProductSummary>;
}): ProductSummary | null {
  if (!input.productId) {
    return null;
  }
  return input.products[input.productId] ?? null;
}

function resolveProductParentId(input: {
  productId: string | null;
  products: Record<string, ProductSummary>;
}): string | null {
  const product = resolveKnownProduct(input);
  return product?.parentProductId ?? null;
}

function isResolvedEntityKind(
  resolved: ResolvedShortId | null,
  type: SessionEntityKind,
): resolved is ResolvedShortId {
  if (!resolved) {
    return false;
  }
  return resolved.type === type;
}
